import { config } from '@/config'
import { paths } from '@/paths'
import { getOperatorCookie, operatorKey } from '@/lib/operator'
import { formatProductSearchSubtitle } from '@/lib/product-size'

export interface AssistantSearchResult {
	transactions: Array<{ type: 'transaction'; id: number; title: string; subtitle?: string }>
	stores: Array<{ type: 'store'; id: number; title: string; subtitle?: string; imageUrl?: string }>
	cabinets: Array<{ type: 'cabinet'; id: string; storeId: number; title: string; subtitle?: string; imageUrl?: string }>
	products: Array<{ type: 'product'; id: number; title: string; subtitle?: string; imageUrl?: string }>
	promotions: Array<{ type: 'promotion'; id: number; title: string; subtitle?: string }>
	marketing: Array<{ type: 'marketing'; id: string; title: string; subtitle?: string }>
	learn: Array<{ type: 'learn'; id: number; title: string; subtitle?: string; url: string }>
}

/** Frontend constructs all route URLs from entity type + id */
export function buildSearchResultHref(result: { type: string; id: string | number; storeId?: number }): string {
	switch (result.type) {
		case 'transaction':
			return paths.dashboard.transactions.preview(Number(result.id))
		case 'store':
			return paths.dashboard.stores.details(Number(result.id))
		case 'cabinet':
			return paths.dashboard.stores.cabinets.details(Number(result.storeId), String(result.id))
		case 'product':
			return paths.dashboard.products.preview(Number(result.id))
		case 'promotion':
			return paths.dashboard.promotions.preview(Number(result.id))
		case 'marketing':
			return paths.dashboard.marketing.preview(String(result.id))
		default:
			return paths.dashboard.overview
	}
}

export interface RecentItem {
	label: string
	href: string
	type: string
	imageUrl?: string
	subtitle?: string
	createdAt: string
}

const RECENT_SEARCHES_KEY = 'micromart:quick-find:recent'
const MAX_RECENT = 5

function getApiBase(): string {
	return config.fridge.apiUrl ?? ''
}

function assistantHeaders(): HeadersInit {
	const headers: Record<string, string> = {}
	const operatorId = getOperatorCookie()
	if (operatorId != null) {
		headers[operatorKey] = String(operatorId)
	}
	if (config.fridge.useSessionHeader) {
		const sessionId = localStorage.getItem('x-session-id')
		if (sessionId) {
			headers['x-session-id'] = sessionId
		}
	}
	return headers
}

function asArray<T>(value: T[] | null | undefined): T[] {
	return Array.isArray(value) ? value : []
}

function normalizeSearchResult(data: Partial<AssistantSearchResult> | null | undefined): AssistantSearchResult {
	return {
		transactions: asArray(data?.transactions),
		stores: asArray(data?.stores),
		cabinets: asArray(data?.cabinets),
		products: asArray(data?.products),
		promotions: asArray(data?.promotions),
		marketing: asArray(data?.marketing),
		learn: asArray(data?.learn),
	}
}

export async function searchAssistant(query: string): Promise<AssistantSearchResult> {
	const params = new URLSearchParams({ q: query })
	try {
		const res = await fetch(`${getApiBase()}/assistant/search?${params}`, {
			credentials: 'include',
			headers: assistantHeaders(),
		})
		if (!res.ok) {
			console.warn('[QuickFind] Search API returned', res.status)
			return normalizeSearchResult(null)
		}
		const data = await res.json()
		const payload = data?.data != null && typeof data.data === 'object' ? data.data : data
		return normalizeSearchResult(payload)
	} catch (err) {
		console.warn('[QuickFind] Search API error', err)
		return normalizeSearchResult(null)
	}
}

export function getRecentItems(): RecentItem[] {
	try {
		const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
		if (!stored) return []
		const parsed = JSON.parse(stored)
		return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : []
	} catch {
		return []
	}
}

export function addRecentItem(item: {
	label: string
	href: string
	type: string
	imageUrl?: string
	subtitle?: string
}): void {
	if (!item.label || !item.href) return
	const existing = getRecentItems()
	const filtered = existing.filter((r) => r.href !== item.href)
	const updated = [{ ...item, createdAt: new Date().toISOString() }, ...filtered].slice(0, MAX_RECENT)
	try {
		localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
	} catch {
		// localStorage full or unavailable
	}
}

export function deleteRecentItem(href: string): void {
	const existing = getRecentItems()
	const filtered = existing.filter((r) => r.href !== href)
	try {
		localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(filtered))
	} catch {
		// localStorage full or unavailable
	}
}

export function clearRecentItems(): void {
	try {
		localStorage.removeItem(RECENT_SEARCHES_KEY)
	} catch {
		// localStorage unavailable
	}
}

function parseRecentProductId(href: string): number | null {
	try {
		const url = new URL(href, window.location.origin)
		if (!url.pathname.includes('/products')) return null
		const previewId = url.searchParams.get('preview_id')
		if (previewId) return Number(previewId)
		const match = url.pathname.match(/\/products\/(\d+)/)
		return match ? Number(match[1]) : null
	} catch {
		return null
	}
}

async function fetchProductForRecent(id: number): Promise<{
	type?: string
	size_value?: number
	size_unit?: string
} | null> {
	try {
		const res = await fetch(`${getApiBase()}/products/${id}`, {
			credentials: 'include',
			headers: assistantHeaders(),
		})
		if (!res.ok) return null
		const data = await res.json()
		const product = data?.data ?? data
		return product && typeof product === 'object' ? product : null
	} catch {
		return null
	}
}

/** Fill in product category/size subtitles for legacy recent items missing them. */
export async function enrichRecentItems(items: RecentItem[]): Promise<RecentItem[]> {
	let changed = false
	const enriched = await Promise.all(
		items.map(async (item) => {
			if (item.type !== 'product' || item.subtitle) return item
			const productId = parseRecentProductId(item.href)
			if (!productId) return item
			const product = await fetchProductForRecent(productId)
			if (!product) return item
			changed = true
			return { ...item, subtitle: formatProductSearchSubtitle(product) }
		})
	)

	if (changed) {
		try {
			localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(enriched))
		} catch {
			// localStorage full or unavailable
		}
	}

	return enriched
}

// --- AI Assistant (Ask AI) ---

export interface AssistantThread {
	id: string
	title: string
	created_at: string
	updated_at?: string
}

export interface AssistantCitation {
	type: 'store' | 'product' | 'transaction' | 'cabinet'
	id: number
	/** Cabinet/site UUID — required when type is cabinet. */
	siteId?: string
	title?: string
	imageUrl?: string
}

export interface AssistantDataSource {
	tools: string[]
	dateFrom?: string
	dateTo?: string
	timezone?: string
}

export interface AssistantContextLabels {
	storeName?: string
	productName?: string
	siteName?: string
}

export type AssistantInputMethod = 'text' | 'voice'

export interface AssistantPersistedMessage {
	id: string
	role: 'user' | 'assistant'
	content: string
	input_method?: AssistantInputMethod | null
	tool_calls?: Array<{
		id: string
		name: string
		status: 'running' | 'done' | 'error'
		result?: string
		args?: Record<string, unknown>
	}> | null
	page_context?: {
		path: string
		pageName?: string
		storeId?: number
		storeName?: string
		productId?: number
		productName?: string
		cabinetId?: number
		siteId?: string
		siteName?: string
		timezone?: string
	} | null
	created_at: string
	feedback_rating?: number | null
}

export type AssistantStreamEvent =
	| { type: 'message_delta'; content: string }
	| { type: 'tool_start'; id: string; name: string }
	| { type: 'tool_end'; id: string; name: string; result?: string; isError?: boolean }
	| {
			type: 'message_done'
			content: string
			messageId?: string
			citations?: AssistantCitation[]
			dataSource?: AssistantDataSource
	  }
	| { type: 'error'; message: string; messageId?: string }

export async function fetchAssistantContextLabels(context: {
	storeId?: number
	productId?: number
	siteId?: string
	cabinetId?: number
}): Promise<AssistantContextLabels> {
	const params = new URLSearchParams()
	if (context.storeId != null) params.set('storeId', String(context.storeId))
	if (context.productId != null) params.set('productId', String(context.productId))
	if (context.siteId) params.set('siteId', context.siteId)
	if (context.cabinetId != null) params.set('cabinetId', String(context.cabinetId))

	const res = await fetch(`${getApiBase()}/assistant/context-labels?${params}`, {
		credentials: 'include',
		headers: assistantHeaders(),
	})
	if (!res.ok) return {}
	return res.json()
}

export interface AssistantProactiveSummary {
	summary: string | null
	suggestedPrompt: string
	metrics?: {
		stockout_count?: number
		overdue_restock_count?: number
		failed_payments_7d?: number
		revenue_change_pct?: number | null
	} | null
	items?: Array<{ category: string; title: string; site_id?: string }>
}

export async function fetchAssistantProactiveSummary(context: {
	storeId?: number
	siteId?: string
	timezone?: string
}): Promise<AssistantProactiveSummary | null> {
	const params = new URLSearchParams()
	if (context.storeId != null) params.set('storeId', String(context.storeId))
	if (context.siteId) params.set('siteId', context.siteId)
	if (context.timezone) params.set('timezone', context.timezone)

	try {
		const res = await fetch(`${getApiBase()}/assistant/proactive-summary?${params}`, {
			credentials: 'include',
			headers: assistantHeaders(),
		})
		if (!res.ok) return null
		return res.json()
	} catch {
		return null
	}
}

export async function enrichAssistantPageContext(context: Record<string, unknown>): Promise<Record<string, unknown>> {
	const res = await fetch(`${getApiBase()}/assistant/page-context/enrich`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			...assistantHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(context),
	})
	if (!res.ok) {
		throw new Error(`Failed to enrich page context (${res.status})`)
	}
	return res.json()
}

async function parseAssistantErrorResponse(res: Response): Promise<string> {
	const raw = await res.text()

	if (res.status === 429) {
		try {
			const body = JSON.parse(raw)
			const retryAfter = Number(body?.retryAfter) || 60
			return `Too many requests. Please wait ${retryAfter} seconds and try again.`
		} catch {
			return 'Too many requests. Please wait a minute and try again.'
		}
	}

	try {
		const body = JSON.parse(raw)
		if (typeof body?.error === 'string') return body.error
		if (typeof body?.message === 'string') return body.message
	} catch {
		// fall through
	}

	return raw || `Request failed (${res.status})`
}

export async function listAssistantThreads(): Promise<AssistantThread[]> {
	const res = await fetch(`${getApiBase()}/assistant/threads`, {
		credentials: 'include',
		headers: assistantHeaders(),
	})
	if (!res.ok) {
		throw new Error('Failed to load threads')
	}
	return res.json()
}

export async function createAssistantThread(title?: string): Promise<AssistantThread> {
	const res = await fetch(`${getApiBase()}/assistant/threads`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json', ...assistantHeaders() },
		body: JSON.stringify({ title }),
	})
	if (!res.ok) {
		throw new Error('Failed to create thread')
	}
	return res.json()
}

export async function deleteAssistantThread(threadId: string): Promise<void> {
	const res = await fetch(`${getApiBase()}/assistant/threads/${threadId}`, {
		method: 'DELETE',
		credentials: 'include',
		headers: assistantHeaders(),
	})
	if (!res.ok) {
		throw new Error('Failed to delete conversation')
	}
}

export async function getAssistantMessages(threadId: string): Promise<AssistantPersistedMessage[]> {
	const res = await fetch(`${getApiBase()}/assistant/threads/${threadId}/messages`, {
		credentials: 'include',
		headers: assistantHeaders(),
	})
	if (!res.ok) {
		throw new Error('Failed to load messages')
	}
	return res.json()
}

export async function submitAssistantFeedback(messageId: string, rating: 1 | -1, comment?: string): Promise<void> {
	const res = await fetch(`${getApiBase()}/assistant/messages/${messageId}/feedback`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json', ...assistantHeaders() },
		body: JSON.stringify({ rating, comment }),
	})
	if (!res.ok) {
		throw new Error('Failed to submit feedback')
	}
}

export async function synthesizeAssistantSpeech(text: string): Promise<Blob> {
	const res = await fetch(`${getApiBase()}/assistant/tts`, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json', ...assistantHeaders() },
		body: JSON.stringify({ text }),
	})
	if (!res.ok) {
		throw new Error('Failed to synthesize speech')
	}
	return res.blob()
}

export async function* streamAssistantMessage(
	threadId: string,
	content: string,
	messages: Array<{ role: 'user' | 'assistant'; content: string }>,
	pageContext?: {
		path: string
		pageName?: string
		storeId?: number
		storeName?: string
		productId?: number
		productName?: string
		cabinetId?: number
		siteId?: string
		siteName?: string
		timezone?: string
	},
	options?: { retry?: boolean; signal?: AbortSignal; inputMethod?: AssistantInputMethod }
): AsyncGenerator<AssistantStreamEvent> {
	const body = options?.retry
		? { retry: true }
		: { content, messages, pageContext, inputMethod: options?.inputMethod ?? 'text' }

	const res = await fetch(`${getApiBase()}/assistant/threads/${threadId}/messages`, {
		method: 'POST',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'text/event-stream',
			...assistantHeaders(),
		},
		body: JSON.stringify(body),
		signal: options?.signal,
	})

	if (!res.ok) {
		const message = await parseAssistantErrorResponse(res)
		yield { type: 'error', message }
		return
	}

	const reader = res.body?.getReader()
	if (!reader) {
		yield { type: 'error', message: 'No response body' }
		return
	}

	const decoder = new TextDecoder()
	let buffer = ''

	while (true) {
		const { done, value } = await reader.read()
		if (done) break

		buffer += decoder.decode(value, { stream: true })
		const parts = buffer.split('\n\n')
		buffer = parts.pop() ?? ''

		for (const part of parts) {
			const lines = part.split('\n')
			let eventType = 'message'
			let data = ''
			for (const line of lines) {
				if (line.startsWith('event: ')) eventType = line.slice(7)
				if (line.startsWith('data: ')) data = line.slice(6)
			}
			if (!data) continue
			try {
				const parsed = JSON.parse(data)
				if (eventType === 'message_delta') {
					yield { type: 'message_delta', content: parsed.content }
				} else if (eventType === 'tool_start') {
					yield { type: 'tool_start', id: parsed.id, name: parsed.name }
				} else if (eventType === 'tool_end') {
					yield {
						type: 'tool_end',
						id: parsed.id,
						name: parsed.name,
						result: parsed.result,
						isError: parsed.isError,
					}
				} else if (eventType === 'message_done') {
					yield {
						type: 'message_done',
						content: parsed.content,
						messageId: parsed.messageId,
						citations: parsed.citations,
						dataSource: parsed.dataSource,
					}
				} else if (eventType === 'error') {
					yield { type: 'error', message: parsed.message, messageId: parsed.messageId }
				}
			} catch {
				// skip malformed chunks
			}
		}
	}
}
